import React, { useEffect } from 'react';
import { Breadcrumbs, Input, Multiselect } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import useEditDealContext from 'contexts/edit-deal.context';
import { ROUTE_KEYS } from 'utils/constants';
import './styles.scss';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as BrandIcon } from 'assets/icons/brands-icon.svg';
import { ReactComponent as EligibilityIcon } from 'assets/icons/customer-icon.svg';
import {
  ENUM_DEAL_ELIGIBILITY_TYPES,
  ENUM_DEAL_REQUIREMENT_TYPES,
  ENUM_DEAL_TYPES,
  ENUM_DEAL_VALUE_TYPES
} from 'utils/enums';
import _ from 'lodash';
import PageLoader from 'components/base/page-loader';
import { generateKey } from 'utils/functions';

const EditDealSettings = () => {
  const {
    editDealForm,
    setEditDealForm,
    handleChange,
    dealValueTypes,
    dealTypes,
    brands,
    dealRequirementTypes,
    dealEligibilityTypes,
    setDisabled,
    users,
    setUserSearchValue,
    initialEditData,
    dealToEdit,
    dealLoading,
    meals
  } = useEditDealContext();

  const breadcrumbs = [
    { name: 'Deals & Discount', path: ROUTE_KEYS.DEALS_AND_DISCOUNTS },
    { name: 'Edit Deal & Discount', path: ROUTE_KEYS.CREATE_DEAL }
  ];

  const isPercentage =
    editDealForm.deal_value_type_id ===
    dealValueTypes.find((t) => t.name === ENUM_DEAL_VALUE_TYPES.PERCENTAGE)?.id;
  const isFixedAmount =
    editDealForm.deal_value_type_id ===
    dealValueTypes.find((t) => t.name === ENUM_DEAL_VALUE_TYPES.FIXED_AMOUNT)?.id;

  const isBrand =
    editDealForm.deal_type_id === dealTypes.find((t) => t.name === ENUM_DEAL_TYPES.BRAND)?.id;
  const isNone =
    editDealForm.deal_requirement_type_id ===
    dealRequirementTypes.find((t) => t.name === ENUM_DEAL_REQUIREMENT_TYPES.NONE)?.id;
  const isMinAmount =
    editDealForm.deal_requirement_type_id ===
    dealRequirementTypes.find((t) => t.name === ENUM_DEAL_REQUIREMENT_TYPES.MINIMUM_PURCHASE_AMOUNT)
      ?.id;
  const isMinItems =
    editDealForm.deal_requirement_type_id ===
    dealRequirementTypes.find(
      (t) => t.name === ENUM_DEAL_REQUIREMENT_TYPES.MINIMUM_QUANTITY_OF_ITEMS
    )?.id;
  const isSpecificCustomers =
    editDealForm.deal_eligibility_type_id ===
    dealEligibilityTypes.find((t) => t.name === ENUM_DEAL_ELIGIBILITY_TYPES.SPECIFIC_CUSTOMERS)?.id;

  const isMeal =
    editDealForm.deal_type_id === dealTypes.find((t) => t.name === ENUM_DEAL_TYPES.MEAL)?.id;

  useEffect(() => {
    if (
      !editDealForm.discount_code ||
      !editDealForm.deal_value_type_id ||
      !editDealForm.cap ||
      !editDealForm.max_usage ||
      !editDealForm.deal_type_id ||
      !editDealForm.deal_requirement_type_id ||
      !editDealForm.deal_eligibility_type_id
    ) {
      setDisabled(true);
    } else {
      const hasChanged = !_.isEqual(initialEditData, editDealForm);
      if (hasChanged) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [editDealForm]);

  const brandOptions = brands.map((b) => ({
    text: b.name,
    value: b.id
  }));

  const mealOptions = meals.map((m) => ({
    text: m.name,
    value: m.id
  }));

  const getLabel = (dealType: string) => {
    switch (dealType) {
      case ENUM_DEAL_TYPES.ALL:
        return 'All brands';
      case ENUM_DEAL_TYPES.BRAND:
        return 'Specific brands';
      case ENUM_DEAL_TYPES.MEAL:
        return 'Specific meals';
      default:
        return '';
    }
  };
  if (dealLoading || !dealToEdit) return <PageLoader />;
  return (
    <div className='mx-10 pt-8 max-w-[700px]'>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText
          title='Edit Deal & Discount'
          description='Edit a new deal lorem dolum ipsum'
        />
      </div>
      <div className='flex items-center gap-2 mb-8'>
        <Input
          type='checkbox'
          name='private'
          checked={editDealForm.private}
          onChange={(e) => handleChange(e)}
        />
        <p className='font-medium'>Make deal private</p>
        <InfoIcon />
      </div>
      <Input
        type='text'
        label='Discount code'
        name='discount_code'
        value={editDealForm.discount_code}
        onChange={(e) => handleChange(e)}
        className='mb-6'
        placeholder='Staffdec23'
        inputClass='h-[50px]'
      />
      <div className='flex items-start gap-4'>
        <div className='mb-6 w-1/2'>
          <label className='flex items-center gap-2 mb-4'>
            <span className='font-medium'>Discount type</span>
            <InfoIcon />
          </label>
          <Multiselect
            isSingle
            placeholder='Select discount type'
            values={editDealForm.deal_value_type_id ? [editDealForm.deal_value_type_id] : []}
            options={dealValueTypes.map((t) => ({
              text: t.name.replaceAll('_', ' '),
              value: t.id
            }))}
            onChange={(types) =>
              setEditDealForm({
                ...editDealForm,
                deal_value_type_id: types?.[0],
                rate: '0',
                fixed_amount: '0'
              })
            }
          />
        </div>
        <Input
          type='number'
          label={`Discount value ${isPercentage ? '(%)' : ''}`}
          name={`${isPercentage ? 'rate' : isFixedAmount ? 'fixed_amount' : ''}`}
          disabled={!isPercentage && !isFixedAmount}
          value={isPercentage ? editDealForm.rate : isFixedAmount ? editDealForm.fixed_amount : ''}
          onChange={(e) => handleChange(e)}
          className='mb-6 w-1/2'
          placeholder='Enter discount value'
          inputClass='h-[50px]'
        />
      </div>
      <div className='flex items-center gap-4'>
        <Input
          type='number'
          label='Deal capped at'
          name='cap'
          value={editDealForm.cap}
          onChange={(e) => handleChange(e)}
          className='mb-6'
          placeholder='1500'
          inputClass='h-[50px]'
        />
        <Input
          type='number'
          label='Usage Limit'
          name='max_usage'
          value={editDealForm.max_usage}
          onChange={(e) => handleChange(e)}
          className='mb-6'
          placeholder='40'
          inputClass='h-[50px]'
        />
      </div>
      <section className='bg-red-10 rounded-md border-[0.5px] border-gray-31 p-5 mb-6'>
        <div className='flex items-center gap-1.5 mb-5'>
          <BrandIcon />
          <p className='text-sm text-gray-32'>Applies to</p>
        </div>
        <div className='flex items-center gap-10 flex-wrap mb-6'>
          {dealTypes.map((t) => (
            <Input
              key={t.id}
              type='radio'
              name='brandsOrAll'
              checked={editDealForm.deal_type_id === t.id}
              onChange={(e) =>
                setEditDealForm({
                  ...editDealForm,
                  deal_type_id: e.target.checked ? t.id : '',
                  brands: []
                })
              }
              label={getLabel(t.name)}
            />
          ))}
        </div>
        {isBrand && (
          <Multiselect
            key={generateKey(brandOptions)}
            placeholder='Select brands'
            values={editDealForm.brands.map((b) => b.id)}
            options={brandOptions}
            hasSearch
            filterResults
            onChange={(ids) =>
              setEditDealForm({
                ...editDealForm,
                brands: ids.map((id) => ({
                  id,
                  name: brandOptions.find((b) => b.value === id)?.text || ''
                }))
              })
            }
          />
        )}
        {isMeal && (
          <div className='mt-7'>
            <Multiselect
              key={generateKey(mealOptions)}
              placeholder='Select meals'
              values={editDealForm.meal_ids}
              options={mealOptions}
              hasSearch
              filterResults
              onChange={(ids) =>
                setEditDealForm({
                  ...editDealForm,
                  meal_ids: ids
                })
              }
            />
          </div>
        )}
      </section>

      <section className='bg-red-10 rounded-md border-[0.5px] border-gray-31 p-5 mb-6'>
        <div className='flex items-center gap-1.5 mb-5'>
          <BrandIcon />
          <p className='text-sm text-gray-32'>Minimum requirements</p>
        </div>
        <div className='flex items-center gap-10 flex-wrap mb-6'>
          {dealRequirementTypes.map((t) => (
            <Input
              key={t.id}
              type='radio'
              name='min_requirements'
              checked={editDealForm.deal_requirement_type_id === t.id}
              onChange={(e) =>
                setEditDealForm({
                  ...editDealForm,
                  deal_requirement_type_id: e.target.checked ? t.id : '',
                  min_amount: '0',
                  min_items: '0'
                })
              }
              label={t.name?.replaceAll('_', ' ').toLowerCase()}
            />
          ))}
        </div>
        {isMinAmount && (
          <Input
            type='number'
            label='Minimum purchase amount (put 0 if none)'
            name='min_amount'
            value={editDealForm.min_amount}
            onChange={(e) => handleChange(e)}
            className='mb-6'
            placeholder='0'
            inputClass='h-[50px]'
            disabled={isNone}
          />
        )}
        {isMinItems && (
          <Input
            type='number'
            label='Minimum quantity of items (Put 0 if none)'
            name='min_items'
            value={editDealForm.min_items}
            onChange={(e) => handleChange(e)}
            className='mb-6'
            placeholder='0'
            inputClass='h-[50px]'
            disabled={isNone}
          />
        )}
      </section>

      <section className='bg-red-10 rounded-md border-[0.5px] border-gray-31 p-5 mb-6'>
        <div className='flex items-center gap-1.5 mb-5'>
          <EligibilityIcon />
          <p className='text-sm text-gray-32'>Customer eligibility</p>
        </div>
        <div className='flex items-center gap-10 flex-wrap mb-6'>
          {dealEligibilityTypes.map((t) => (
            <Input
              key={t.id}
              type='radio'
              name='eligibility'
              checked={editDealForm.deal_eligibility_type_id === t.id}
              onChange={(e) =>
                setEditDealForm({
                  ...editDealForm,
                  deal_eligibility_type_id: e.target.checked ? t.id : ''
                })
              }
              label={t.name?.replaceAll('_', ' ').toLowerCase()}
            />
          ))}
        </div>
        {isSpecificCustomers && (
          <Multiselect
            hasSearch
            placeholder='Select group'
            onSearchChange={(value) => setUserSearchValue(value)}
            values={editDealForm.specific_customers}
            options={users.map((u) => ({
              text: `${u.first_name} ${u.last_name}`,
              value: u.id
            }))}
            onChange={(ids) =>
              setEditDealForm({
                ...editDealForm,
                specific_customers: ids
              })
            }
          />
        )}
      </section>
      <section className='rounded-lg border border-gray-33'>
        <p className='pt-4 pb-3 px-6 text-gray-6 text-sm border-b border-gray-33'>Extras</p>
        <div className='px-6 py-4 flex items-center gap-5 flex-wrap'>
          <Input
            type='checkbox'
            name='notification'
            label='Send as push notification'
            checked={editDealForm.notification}
            onChange={(e) => handleChange(e)}
          />
          <Input
            type='checkbox'
            name='active'
            label='Set deal as visible'
            checked={editDealForm.active}
            onChange={(e) => handleChange(e)}
          />
          <Input
            type='checkbox'
            name='post'
            label='Add to home page header'
            checked={editDealForm.post}
            onChange={(e) => handleChange(e)}
          />
        </div>
      </section>
    </div>
  );
};

export default EditDealSettings;
